import { useEffect, useState } from 'react';
import { Api } from '@infra/api';
import { Group, Gender } from '@domain/models';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';

const AGE_UNIT_OPTIONS = [
  { value: 'days', label: 'дней' },
  { value: 'months', label: 'мес' },
  { value: 'years', label: 'год' },
];

const ageUnitLabel = (unit?: string | null) => AGE_UNIT_OPTIONS.find(option => option.value === unit)?.label || 'год';
const genderLabel = (value?: string | null) => value === 'any' ? 'Любой' : value || '—';
const ageRangeLabel = (group: Group) =>
  `${group.groups_min_age} ${ageUnitLabel(group.groups_min_age_unit)} – ${group.groups_max_age} ${ageUnitLabel(group.groups_max_age_unit)}`;

const empty: Group = { groups_name:'', gender:'any', groups_min_age:0, groups_max_age:120, groups_min_age_unit:'years', groups_max_age_unit:'years' };

export function GroupsPage() {
  const [items, setItems] = useState<Group[]>([]);
  const [form, setForm] = useState<Group>({ ...empty });
  const [genders, setGenders] = useState<Gender[]>([]);
  const [selected, setSelected] = useState<Group & { id: number } | null>(null);

  useEffect(() => {
    Api.listGroups().then(setItems).catch(console.error);
    Api.listGenders().then(setGenders).catch(console.error);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const created = await Api.createGroup(form);
    setItems([created, ...items]);
    setForm({ ...empty });
  }

  async function saveSelected() {
    if (!selected || selected.id == null) return;
    const updated = await Api.updateGroup(selected.id, {
      groups_name: selected.groups_name,
      gender: selected.gender,
      groups_min_age: selected.groups_min_age,
      groups_max_age: selected.groups_max_age,
      groups_min_age_unit: selected.groups_min_age_unit || 'years',
      groups_max_age_unit: selected.groups_max_age_unit || 'years',
    });
    setItems(items.map(g => (g.id === updated.id ? updated : g)));
    setSelected(null);
  }

  return (
    <div>
      <div className="toolbar">
        <div className="title"><h2 style={{margin:0}}>Группы</h2></div>
      </div>
      <div className="row">
        <form className="card" onSubmit={submit} style={{maxWidth:560}}>
          <h3 style={{marginTop:0}}>Новая группа</h3>
          <label>Название</label>
          <input value={form.groups_name} onChange={e=>setForm({...form, groups_name:e.target.value})} required />
          <label>Пол</label>
          <select value={form.gender} onChange={e=>setForm({...form, gender:e.target.value})}>
            {genders.map(g => <option key={g.genders_name} value={g.genders_name}>{g.genders_description}</option>)}
          </select>
          <div className="row">
            <div>
              <label>Мин. возраст</label>
              <input type="number" value={form.groups_min_age} onChange={e=>setForm({...form, groups_min_age:Number(e.target.value)})} />
            </div>
            <div>
              <label>Ед.</label>
              <select value={form.groups_min_age_unit || 'years'} onChange={e=>setForm({...form, groups_min_age_unit:e.target.value})}>
                {AGE_UNIT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div>
              <label>Макс. возраст</label>
              <input type="number" value={form.groups_max_age} onChange={e=>setForm({...form, groups_max_age:Number(e.target.value)})} />
            </div>
            <div>
              <label>Ед.</label>
              <select value={form.groups_max_age_unit || 'years'} onChange={e=>setForm({...form, groups_max_age_unit:e.target.value})}>
                {AGE_UNIT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{marginTop:12}}>
            <button>Создать</button>
          </div>
        </form>

        <div style={{flex:2}}>
          <DataTable
            data={items}
            title="Список"
            searchFields={['groups_name', 'gender']}
            searchPlaceholder="Поиск по названию группы, полу..."
            onRowClick={(item) => item.id != null && setSelected(item as Group & {id:number})}
            columns={[
              { key: 'groups_name', title: 'Название' },
              { key: 'gender', title: 'Пол', render: (item) => genderLabel(item.gender) },
              { 
                key: 'age_range', 
                title: 'Возраст',
                render: (item) => ageRangeLabel(item)
              }
            ]}
          />
        </div>
      </div>

      {selected && (
        <Modal title={`Группа: ${selected.groups_name}`} onClose={()=>setSelected(null)}>
          <div>
            <label>Название</label>
            <input value={selected.groups_name} onChange={e=>setSelected({...selected!, groups_name:e.target.value})} />
            <label>Пол</label>
            <select value={selected.gender} onChange={e=>setSelected({...selected!, gender:e.target.value})}>
              {genders.map(g => <option key={g.genders_name} value={g.genders_name}>{g.genders_description}</option>)}
            </select>
            <div className="row">
              <div>
                <label>Мин. возраст</label>
                <input type="number" value={selected.groups_min_age} onChange={e=>setSelected({...selected!, groups_min_age:Number(e.target.value)})} />
              </div>
              <div>
                <label>Ед.</label>
                <select value={selected.groups_min_age_unit || 'years'} onChange={e=>setSelected({...selected!, groups_min_age_unit:e.target.value})}>
                  {AGE_UNIT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>
              <div>
                <label>Макс. возраст</label>
                <input type="number" value={selected.groups_max_age} onChange={e=>setSelected({...selected!, groups_max_age:Number(e.target.value)})} />
              </div>
              <div>
                <label>Ед.</label>
                <select value={selected.groups_max_age_unit || 'years'} onChange={e=>setSelected({...selected!, groups_max_age_unit:e.target.value})}>
                  {AGE_UNIT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{marginTop:12, display:'flex', gap:8}}>
              <button onClick={saveSelected}>Сохранить</button>
              <button className="secondary" onClick={()=>setSelected(null)}>Отмена</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


